/**
 * Collapses long PDP descriptions behind a See more / See less toggle.
 * Uses max-height clipping so all paragraphs stay hidden when collapsed.
 */
class ProductDescriptionExpandable extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement | null} */
  #content = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-expandable-viewport]');
    this.#content = this.querySelector('[data-expandable-content]');
    this.#button = this.querySelector('[data-expandable-toggle]');

    if (!this.#viewport || !this.#content || !this.#button) return;

    this.#button.addEventListener('click', this.#toggle);
    this.#applyCollapsedState();
    this.#checkOverflow();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.hasAttribute('data-expanded')) return;
        this.#applyCollapsedState();
        this.#checkOverflow();
      });
      this.#resizeObserver.observe(this.#content);
    }
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#toggle);
    this.#resizeObserver?.disconnect();
  }

  /**
   * @returns {number}
   */
  #getLineCount() {
    const lines = parseInt(getComputedStyle(this).getPropertyValue('--pde-lines'), 10);
    return Number.isFinite(lines) && lines > 0 ? lines : 4;
  }

  /**
   * Measure the pixel height of the first N lines of text.
   *
   * @param {number} lineCount
   * @returns {number}
   */
  #measureCollapsedHeight(lineCount) {
    if (!this.#content || !this.#viewport) return 0;

    this.#viewport.style.maxHeight = 'none';

    const width = this.#content.clientWidth;
    if (!width) {
      const lineHeight = this.#getLineHeight(this.#content);
      return Math.ceil(lineHeight * lineCount);
    }

    const probe = this.#content.cloneNode(true);
    probe.removeAttribute('data-expandable-content');
    probe.style.cssText = [
      'position:absolute',
      'left:0',
      'top:0',
      'visibility:hidden',
      'pointer-events:none',
      'overflow:visible',
      'max-height:none',
      'height:auto',
      `width:${width}px`,
    ].join(';');

    this.#viewport.appendChild(probe);

    const blocks = this.#getTextBlocks(probe);
    let height = 0;

    if (blocks.length === 0) {
      height = Math.ceil(this.#getLineHeight(probe) * lineCount);
    } else {
      const targetLine = Math.min(lineCount, blocks.length);
      const lastRect = blocks[targetLine - 1];
      const firstRect = blocks[0];
      height = Math.ceil(lastRect.bottom - firstRect.top);
    }

    probe.remove();

    return height;
  }

  /**
   * @param {HTMLElement} root
   * @returns {DOMRect[]}
   */
  #getTextBlocks(root) {
    /** @type {DOMRect[]} */
    const lines = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        return node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    let node = walker.nextNode();
    while (node) {
      const text = node.textContent || '';
      for (let index = 0; index < text.length; index += 1) {
        const range = document.createRange();
        range.setStart(node, index);
        range.setEnd(node, index + 1);
        const rect = range.getBoundingClientRect();
        if (!rect.height) continue;

        const lastLine = lines[lines.length - 1];
        if (!lastLine || Math.abs(rect.top - lastLine.top) > 1) {
          lines.push(rect);
        } else if (rect.bottom > lastLine.bottom) {
          lines[lines.length - 1] = rect;
        }
      }
      node = walker.nextNode();
    }

    return lines;
  }

  /**
   * @param {HTMLElement} element
   * @returns {number}
   */
  #getLineHeight(element) {
    const style = getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight);

    if (Number.isFinite(lineHeight)) return lineHeight;

    const fontSize = parseFloat(style.fontSize);
    return Number.isFinite(fontSize) ? fontSize * 1.5 : 24;
  }

  #applyCollapsedState() {
    if (!this.#viewport || this.hasAttribute('data-expanded')) return;

    const collapsedHeight = this.#measureCollapsedHeight(this.#getLineCount());
    this.#viewport.style.maxHeight = `${collapsedHeight}px`;
  }

  #clearCollapsedState() {
    if (!this.#viewport) return;
    this.#viewport.style.removeProperty('max-height');
  }

  #checkOverflow = () => {
    if (!this.#viewport || !this.#content || !this.#button) return;
    if (this.hasAttribute('data-expanded')) return;

    this.#clearCollapsedState();
    const fullHeight = this.#content.getBoundingClientRect().height;

    this.#applyCollapsedState();
    const collapsedHeight = this.#viewport.getBoundingClientRect().height;

    const overflowing = fullHeight > collapsedHeight + 1;
    this.#button.hidden = !overflowing;
  };

  #toggle = () => {
    if (!this.#button) return;

    const expanded = this.toggleAttribute('data-expanded');
    this.#button.setAttribute('aria-expanded', String(expanded));

    if (expanded) {
      this.#clearCollapsedState();
    } else {
      this.#applyCollapsedState();
      requestAnimationFrame(() => this.#checkOverflow());
    }
  };
}

if (!customElements.get('product-description-expandable')) {
  customElements.define('product-description-expandable', ProductDescriptionExpandable);
}
